import { useCallback, useMemo } from 'react';
import { useBearAuth } from '@bear-auth/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import config from '@/config';
import { actions as messagesActions } from '@/modules/messages';
import type { ConfirmationModalProps } from '@/modules/modals/components/ConfirmationModal';

import { useAccessDeniedStore } from '../../../hooks';
import { useRequestAccess } from './useRequestAccess';

export const useRequestAccessPageContent = () => {
    const { name, clearAccessDenied } = useAccessDeniedStore();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {
        actions: { logout },
    } = useBearAuth();

    const { mutate, isPending, isSuccess } = useRequestAccess();

    const { formatMessage } = useIntl();

    const onCancel = useCallback(async () => {
        await logout();
        clearAccessDenied();
        navigate(config.routes.home);
    }, [logout, clearAccessDenied, navigate]);

    const content = useMemo<ConfirmationModalProps>(() => {
        if (isSuccess) {
            return {
                title: formatMessage({ id: 'auth.accessDenied.requestAccess.success.title' }),
                children: <FormattedMessage id='auth.accessDenied.requestAccess.success.description' />,
                cancelButton: {
                    onClick: onCancel,
                    children: formatMessage({ id: 'auth.accessDenied.requestAccess.success.backButton' }),
                },
            };
        }

        return {
            title: formatMessage({ id: 'auth.accessDenied.requestAccess.request.title' }),
            children: (
                <>
                    <p>
                        <FormattedMessage id='auth.accessDenied.requestAccess.request.description.1' />
                    </p>

                    {name && (
                        <p>
                            <FormattedMessage
                                id='auth.accessDenied.requestAccess.request.description.2'
                                values={{ name, b: chunks => <strong>{chunks}</strong> }}
                            />
                        </p>
                    )}
                </>
            ),
            okButton: {
                onClick: () =>
                    mutate(undefined, {
                        onError: () =>
                            dispatch(
                                messagesActions.displayErrorMessage({
                                    message: formatMessage({ id: 'auth.accessDenied.requestAccess.error' }),
                                    options: {
                                        duration: 5,
                                    },
                                }),
                            ),
                    }),
                loading: isPending,
                children: formatMessage({ id: 'auth.accessDenied.requestAccess.request.requestButton' }),
            },
            cancelButton: {
                onClick: onCancel,
                children: formatMessage({ id: 'cancel' }),
            },
        };
    }, [isSuccess, formatMessage, isPending, mutate, onCancel, name, dispatch]);

    return content;
};
